// Package imports:
import React, { useEffect, useMemo, useState } from 'react';
import { faFilePdf } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleRight } from '@fortawesome/pro-regular-svg-icons';
// Component imports:
import Alert from '../../../ui-elements/Alert/Alert';
import Button from '../../../ui-elements/Button/Button';
import Label from '../../../ui-elements/Label/Label';
import Link from '../../../ui-elements/Link/Link';
import Loading from '../../../ui-elements/Loading/Loading';
import DisplayBox from '../../DisplayBox/DisplayBox';
// Service imports:
import { GET_KELDAN_API_URL } from '../../../services/config';
import { getKeldanDateString } from '../../../services/utils';
import { useBuyingProcessUrl } from '../../../services/buyhook';
import { ErrorMessages } from '../../../services/errorMessages';
// Type imports:
import { Fetched } from '../../../types/Types';
import { ICompanyViewModel, getDate, KeldanPaper, ReportsSearchModel } from '../../../types/CompanyTypes';

interface IOwnProps {
    limit?: number,
}

const CompanyPapers: React.FC<ICompanyViewModel & IOwnProps> = ({
    id,
    isAuthenticated,
    limit,
}) => {
    const [data, setData] = useState<Fetched<ReportsSearchModel>>(null);
    
    const fetchData = async () => {
        try {
            const url = `${GET_KELDAN_API_URL()}/Company/GetCompanyPapersAndStatements/${id}`;
            const headers = {
                'Content-Type': 'application/json; charset=utf-8'
            };
            
            const response = await fetch(url, {
                method: 'GET',
                headers
            });
            if (response.ok) {
                if (response.redirected) {
                    window.location.href = response.url;
                } else {
                    const body = await response.json();
                    setData(body);
                }
            } else {
                setData(new Error(ErrorMessages.RequestFailed));
            }
        } catch (e) {
            setData(new Error(ErrorMessages.OtherShort));
        }
    }

    useEffect(() => {
        fetchData();
    }, [isAuthenticated]);

    const { tryPurchaseItem, isItemLoading, ModalsAndToasts } = useBuyingProcessUrl(
        (paper: KeldanPaper, modalReference: string) => {
            const url = `${GET_KELDAN_API_URL()}/Pdf/Fyrirtaekjaskra-Kaupa?fileId=${paper.id}&ssn=${id}&paperType=${paper.type}&year=${paper.collected.year}&reference=${modalReference}`;
            return url;
        },
        () => {
            window.setTimeout(() => {
                fetchData();
            }, 5*1000);
        },
        'fyrirtækjaskrá'
    );

    const papersToShow = useMemo(() => {
        if (data instanceof Error) return null;
        if (data?.papers === undefined || data.papers === null) return null;
        return data?.papers[0]?.papers?.sort((a, b) => {
            const aDate = getDate(a.collected);
            const bDate = getDate(b.collected);
            return aDate === null && bDate === null
                ? 0
                : aDate === null
                ? 1
                : bDate === null
                ? -1
                : aDate < bDate
                ? 1
                : aDate > bDate
                ? -1
                : 0;
        }).slice(0, limit);
    }, [data]);

    const getOwned = (paper: KeldanPaper) => {
        if (data instanceof Error) return undefined;
        if (isAuthenticated) {
            const or = data?.existing?.find((e) => e.fileId === paper.id);
            return or;
        }
        return null;
    }

    return (
        <DisplayBox 
            className='KCL_company-papers'
            title="Gögn úr fyrirtækjaskrá"
            asideComponent={<Label labelType='origin' text='RSK' />}
        >
            {(data instanceof Error)
                ? <Alert type='error' headText={data.message} />
                : (data === null)
                ? <Loading />
                : papersToShow?.length === 0
                ? <Alert type='info' headText='Engin gögn í boði' />
                : <div>
                    {papersToShow?.map((paper, index ) => {
                        const owned = getOwned(paper);
                        return <div key={index} className='company-paper-line'>
                            <div className="main-line">
                                {(owned)
                                    ? <Link
                                        linkSize='15'
                                        url={isAuthenticated && owned?.balls !== undefined
                                            ? `/Pdf/Fyrirtaekjaskra?eventId=${owned?.balls}&ssn=${id}`
                                            : undefined
                                        }
                                        targetBlank
                                        style={{marginBottom: '10px'}}
                                    >
                                        <FontAwesomeIcon icon={faFilePdf} />
                                        {paper.type}
                                    </Link>
                                    : <>
                                        <Link
                                            linkSize='15'
                                            disabled
                                        >
                                            <FontAwesomeIcon icon={faFilePdf} />
                                            {paper.type}
                                        </Link>
                                        <Button
                                            showLoader={isItemLoading(paper)}
                                            size='sm'
                                            buttonType='buy'
                                            onClick={() => tryPurchaseItem(paper, 'confirm-modal-with-reference')}
                                        >
                                            Kaupa
                                        </Button>
                                    </>
                                }
                            </div>
                            <div className="dates-line">
                                <span className='collected-date'>
                                    Safnað {getKeldanDateString(paper.collected, 'DD/MM/YYYY', '.')}
                                </span>
                            </div>
                        </div>
                    })}
                </div>
            }
            <Link
                linkSize='14'
                url={`/Fyrirtaeki/Fyrirtaekjaskra/${id}`}
                className='more-link'
            >
                <FontAwesomeIcon icon={faAngleRight} />
                Fleiri gögn
            </Link>
            <ModalsAndToasts />
        </DisplayBox>
    );
}

export default CompanyPapers;